import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  ArrowLeft, RefreshCw, Download, Globe, Lock, ShieldCheck, Server, Network,
  Clock, History, ArrowUpRight, Loader2, FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEALTH = {
  saudavel: { label: 'SAUDÁVEL', className: 'bg-green-500/15 text-green-500 border-green-500/30' },
  atencao: { label: 'ATENÇÃO', className: 'bg-amber-500/15 text-amber-500 border-amber-500/30' },
  critico: { label: 'CRÍTICO', className: 'bg-red-500/15 text-red-500 border-red-500/30' },
};

const STATUS_STYLE = {
  presente: 'text-green-500', fraco: 'text-amber-500',
  ausente: 'text-red-500', permissivo: 'text-amber-500',
};

const EVENT_KINDS = {
  all: 'Todos os tipos', certificado: 'Certificado', registro: 'Registro',
  dns: 'DNS', infra: 'Infraestrutura', subdominio: 'Subdomínios',
  cabecalho: 'Cabeçalhos', email: 'E-mail (SPF/DMARC)',
};

const fmtDate = (v) => (v ? new Date(v).toLocaleString('pt-BR') : '—');

const Field = ({ label, value, accent }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-border/50 last:border-0">
    <span className="text-muted-foreground text-sm">{label}</span>
    <span className={`text-sm text-right ${accent || ''}`}>{value ?? '—'}</span>
  </div>
);

const Section = ({ title, icon: Icon, children, action }) => (
  <Card>
    <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
      <CardTitle className="text-base flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-primary" />} {title}
      </CardTitle>
      {action}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

export default function DomainDossierPage() {
  const { domainId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState('24h');
  const [eventKind, setEventKind] = useState('all');
  const { getAuthHeader, user } = useAuthStore();
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/webmonitor/domains/${domainId}/dossier`,
        { headers: authRef.current() });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar o dossiê');
    } finally {
      setLoading(false);
    }
  }, [domainId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refreshNow = async () => {
    setBusy(true);
    toast.info('Coletando DNS, certificado, cabeçalhos, registro e subdomínios...');
    try {
      await axios.post(`${API}/webmonitor/domains/${domainId}/refresh`, {},
        { headers: authRef.current(), timeout: 240000 });
      toast.success('Coleta concluída');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na coleta');
    } finally {
      setBusy(false);
    }
  };

  const exportDossier = async (fmt) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/webmonitor/domains/${domainId}/export`,
        { formats: [fmt] }, { headers: authRef.current(), timeout: 180000 });
      const job = res.data;
      const file = await axios.get(`${API}/reports/${job.id}/download/${fmt}`,
        { headers: authRef.current(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([file.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', job.files[fmt]);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Dossiê ${fmt.toUpperCase()} gerado e salvo na Biblioteca de Relatórios`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao exportar o dossiê');
    } finally {
      setBusy(false);
    }
  };

  const promote = async (names) => {
    try {
      const res = await axios.post(`${API}/webmonitor/domains/${domainId}/promote-bulk`,
        { subdomains: names }, { headers: authRef.current() });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao promover subdomínios');
    }
  };

  const runMonitor = async (m) => {
    try {
      const res = await axios.post(`${API}/webmonitor/monitors/${m.id}/run`, {},
        { headers: authRef.current() });
      toast.success(`${m.name}: ${res.data.message}`);
      fetchData();
    } catch (_) { toast.error('Erro ao verificar monitor'); }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-16 w-full" /><Skeleton className="h-96 w-full" /></div>;
  }
  if (!data) return null;

  const i = data.intel || {};
  const ea = i.email_auth || {};
  const health = HEALTH[data.health] || HEALTH.saudavel;
  const hours = range === '24h' ? 24 : range === '7d' ? 168 : 720;
  const cutoff = Date.now() - hours * 3600 * 1000;
  const series = (data.checks || [])
    .filter(c => new Date(c.created_at).getTime() >= cutoff)
    .map(c => ({
      time: new Date(c.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      latency: c.latency_ms || 0,
      up: c.status === 'up' ? 100 : 0,
    }));
  const unmonitored = (data.subdomains || []).filter(s => !s.monitored);
  const events = (data.events || []).filter(e => eventKind === 'all' || e.kind === eventKind);

  return (
    <div className="space-y-6" data-testid="domain-dossier-page">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 -mx-4 px-4 py-3 bg-background/95 backdrop-blur border-b">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <button onClick={() => navigate('/dashboard/webmonitor')}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1"
              data-testid="back-to-webmonitor">
              <ArrowLeft className="h-3 w-3" /> Web Monitor
            </button>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" /> {data.domain}
              <Badge variant="outline" className={health.className} data-testid="dossier-health">{health.label}</Badge>
              {(data.collecting || busy) && (
                <Badge variant="secondary" className="gap-1"><Loader2 className="h-3 w-3 animate-spin" />coletando</Badge>
              )}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {data.org_name} · última coleta {fmtDate(data.last_collect)} · próxima prevista {fmtDate(data.next_collect)}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={refreshNow} disabled={busy}
              data-testid="dossier-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${busy ? 'animate-spin' : ''}`} /> Atualizar agora
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportDossier('csv')} disabled={busy}
              data-testid="dossier-export-csv">
              <Download className="h-4 w-4 mr-2" /> CSV
            </Button>
            <Button size="sm" onClick={() => exportDossier('pdf')} disabled={busy}
              data-testid="dossier-export-pdf">
              <FileText className="h-4 w-4 mr-2" /> Dossiê PDF
            </Button>
          </div>
        </div>
      </div>

      {/* a) Resumo */}
      <div className="grid gap-3 md:grid-cols-5">
        {[
          { label: 'Dias p/ expirar SSL', value: i.ssl_days_left ?? '—', warn: i.ssl_days_left <= 30 },
          { label: 'Dias p/ expirar domínio', value: i.domain_days_left ?? '—', warn: i.domain_days_left <= 30 },
          { label: 'Monitores online', value: `${(data.monitors || []).filter(m => m.last_status === 'up').length}/${(data.monitors || []).length}` },
          { label: 'Subdomínios', value: i.subdomains_total ?? 0 },
          { label: 'Apontamentos de risco', value: (data.risks || []).length, warn: (data.risks || []).length > 0 },
        ].map(card => (
          <Card key={card.label}>
            <CardContent className="p-4">
              <div className={`text-2xl font-bold ${card.warn ? 'text-amber-500' : ''}`}>{card.value}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{card.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      {(data.risks || []).length > 0 && (
        <Card className="border-amber-500/40 bg-amber-500/5" data-testid="dossier-risks">
          <CardContent className="p-4 flex flex-wrap gap-2 items-center">
            <span className="text-sm font-medium mr-1">Apontamentos:</span>
            {data.risks.map(r => <Badge key={r} variant="outline" className="text-amber-600 border-amber-500/40">{r}</Badge>)}
          </CardContent>
        </Card>
      )}

      {/* b) Disponibilidade */}
      <Section title="Disponibilidade e desempenho" icon={Clock} action={
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-[130px] h-8" data-testid="dossier-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Últimas 24h</SelectItem>
            <SelectItem value="7d">Últimos 7 dias</SelectItem>
            <SelectItem value="30d">Últimos 30 dias</SelectItem>
          </SelectContent>
        </Select>
      }>
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div><div className="text-2xl font-bold">{data.uptime ?? '—'}%</div>
            <div className="text-xs text-muted-foreground uppercase">Uptime</div></div>
          <div><div className="text-2xl font-bold">{data.avg_latency_ms ?? '—'} ms</div>
            <div className="text-xs text-muted-foreground uppercase">Latência média</div></div>
          <div><div className="text-2xl font-bold">{(data.incidents || []).filter(x => x.status === 'open').length}</div>
            <div className="text-xs text-muted-foreground uppercase">Incidentes abertos</div></div>
        </div>
        {series.length > 1 ? (
          <div className="h-56" data-testid="dossier-latency-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="latency" name="Latência (ms)" stroke="#F97316" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="up" name="Disponível (%)" stroke="#16A34A" dot={false} strokeWidth={1} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Sem verificações suficientes no período selecionado</p>
        )}

        <h4 className="text-sm font-medium mt-5 mb-2">Monitores vinculados</h4>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Monitor</TableHead><TableHead>Tipo</TableHead><TableHead>Destino</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Ação</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data.monitors || []).map(m => (
              <TableRow key={m.id}>
                <TableCell className="font-medium">{m.name}</TableCell>
                <TableCell>{m.type}</TableCell>
                <TableCell className="font-mono text-xs">{m.target}</TableCell>
                <TableCell>
                  <Badge variant={m.last_status === 'up' ? 'default' : m.last_status === 'down' ? 'destructive' : 'outline'}>
                    {m.last_status || 'aguardando'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => runMonitor(m)}
                    data-testid={`dossier-run-monitor-${m.id}`}>Verificar agora</Button>
                </TableCell>
              </TableRow>
            ))}
            {(data.monitors || []).length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-5 text-sm text-muted-foreground">
                Nenhum monitor vinculado a este domínio
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>

        <h4 className="text-sm font-medium mt-5 mb-2">Incidentes</h4>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Monitor</TableHead><TableHead>Início</TableHead><TableHead>Fim</TableHead>
            <TableHead>Status</TableHead><TableHead>Mensagem</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data.incidents || []).slice(0, 25).map(inc => (
              <TableRow key={inc.id}>
                <TableCell>{inc.monitor_name}</TableCell>
                <TableCell className="text-sm">{fmtDate(inc.started_at)}</TableCell>
                <TableCell className="text-sm">{inc.resolved_at ? fmtDate(inc.resolved_at) : '—'}</TableCell>
                <TableCell>
                  <Badge variant={inc.status === 'open' ? 'destructive' : 'outline'}>
                    {inc.status === 'open' ? 'Em aberto' : 'Resolvido'}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">{inc.message}</TableCell>
              </TableRow>
            ))}
            {(data.incidents || []).length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-5 text-sm text-muted-foreground">
                Nenhum incidente registrado
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Section>

      {/* c) Certificado */}
      <Section title="Certificado" icon={Lock}>
        <div className="grid md:grid-cols-2 gap-x-8">
          <div>
            <Field label="Emissor" value={i.ssl_issuer} />
            <Field label="Titular" value={i.ssl_subject} />
            <Field label="Validade" value={i.ssl_valid_until} />
            <Field label="Dias restantes" value={i.ssl_days_left}
              accent={i.ssl_days_left <= 30 ? 'text-amber-500 font-medium' : ''} />
          </div>
          <div>
            <Field label="Protocolo negociado" value={i.ssl_protocol} />
            <Field label="Cifra" value={i.tls_cipher} />
            <Field label="Nome compatível"
              value={i.ssl_name_match === false ? 'Incompatível' : i.ssl_name_match ? 'Sim' : '—'}
              accent={i.ssl_name_match === false ? 'text-red-500' : ''} />
            <Field label="Protocolos aceitos" value={(i.tls_versions || []).join(', ') || '—'} />
            <Field label="Protocolos obsoletos" value={(i.tls_obsolete || []).join(', ') || 'nenhum'}
              accent={(i.tls_obsolete || []).length ? 'text-red-500' : ''} />
          </div>
        </div>

        <h4 className="text-sm font-medium mt-5 mb-2">Cadeia do certificado</h4>
        <Table data-testid="dossier-cert-chain">
          <TableHeader><TableRow>
            <TableHead>Posição</TableHead><TableHead>Titular</TableHead><TableHead>Emissor</TableHead>
            <TableHead>Válido de</TableHead><TableHead>Válido até</TableHead><TableHead className="text-center">Dias</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(i.ssl_chain || []).map(c => (
              <TableRow key={c.fingerprint || c.subject}>
                <TableCell><Badge variant="outline">{c.position}</Badge></TableCell>
                <TableCell className="text-sm">{c.subject}</TableCell>
                <TableCell className="text-sm">{c.issuer}</TableCell>
                <TableCell className="text-sm">{c.valid_from}</TableCell>
                <TableCell className="text-sm">{c.valid_until}</TableCell>
                <TableCell className="text-center text-sm">{c.days_left}</TableCell>
              </TableRow>
            ))}
            {(i.ssl_chain || []).length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-5 text-sm text-muted-foreground">
                Cadeia não disponível
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>

        <h4 className="text-sm font-medium mt-4 mb-1">Nomes cobertos (SANs)</h4>
        <div className="flex flex-wrap gap-1">
          {(i.ssl_sans || []).map(s => <Badge key={s} variant="secondary" className="text-xs font-mono">{s}</Badge>)}
          {(i.ssl_sans || []).length === 0 && <span className="text-sm text-muted-foreground">—</span>}
        </div>
      </Section>

      {/* d) Cabeçalhos */}
      <Section title="Cabeçalhos de segurança" icon={ShieldCheck}>
        <Table data-testid="dossier-headers">
          <TableHeader><TableRow>
            <TableHead>Cabeçalho</TableHead><TableHead>Situação</TableHead><TableHead>Valor</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(i.security_headers || []).map(h => (
              <TableRow key={h.header}>
                <TableCell className="font-medium">{h.header}</TableCell>
                <TableCell>
                  <span className={`text-xs uppercase font-semibold ${STATUS_STYLE[h.status] || ''}`}>
                    {h.status}
                  </span>
                </TableCell>
                <TableCell className="text-xs font-mono max-w-[420px] truncate">{h.value}</TableCell>
              </TableRow>
            ))}
            {(i.security_headers || []).length === 0 && (
              <TableRow><TableCell colSpan={3} className="text-center py-5 text-sm text-muted-foreground">
                Não foi possível ler os cabeçalhos ({i.security_headers_error || 'sem resposta HTTPS'})
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Section>

      {/* e) DNS */}
      <Section title="DNS completo" icon={Network}>
        <div className="grid md:grid-cols-2 gap-x-8">
          {Object.entries(i.dns || {}).map(([type, values]) => (
            <Field key={type} label={type} value={(values || []).join(', ') || '—'} />
          ))}
        </div>
        <h4 className="text-sm font-medium mt-5 mb-2">Autenticação de e-mail</h4>
        <Table data-testid="dossier-email-auth">
          <TableHeader><TableRow>
            <TableHead>Mecanismo</TableHead><TableHead>Situação</TableHead><TableHead>Valor</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">SPF</TableCell>
              <TableCell><span className={`text-xs uppercase font-semibold ${STATUS_STYLE[ea.spf_status] || ''}`}>
                {ea.spf_status || 'ausente'}</span></TableCell>
              <TableCell className="text-xs font-mono max-w-[420px] truncate">{ea.spf || '—'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">DMARC</TableCell>
              <TableCell><span className={`text-xs uppercase font-semibold ${STATUS_STYLE[ea.dmarc_status] || ''}`}>
                {ea.dmarc_status || 'ausente'}</span></TableCell>
              <TableCell className="text-xs font-mono max-w-[420px] truncate">{ea.dmarc || '—'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">DKIM</TableCell>
              <TableCell><span className={`text-xs uppercase font-semibold ${ea.dkim_selectors?.length ? 'text-green-500' : 'text-amber-500'}`}>
                {ea.dkim_status || '—'}</span></TableCell>
              <TableCell className="text-xs">{(ea.dkim_selectors || []).join(', ') || '—'}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Section>

      {/* f) Infraestrutura */}
      <Section title="Infraestrutura e registro" icon={Server}>
        <Table data-testid="dossier-ip-intel">
          <TableHeader><TableRow>
            <TableHead>IP</TableHead><TableHead>ASN</TableHead><TableHead>Organização</TableHead><TableHead>País</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(i.ip_intel || []).map(x => (
              <TableRow key={x.ip}>
                <TableCell className="font-mono text-sm">{x.ip}</TableCell>
                <TableCell className="text-sm">{x.asn}</TableCell>
                <TableCell className="text-sm">{x.org}</TableCell>
                <TableCell className="text-sm">{x.country}</TableCell>
              </TableRow>
            ))}
            {(i.ip_intel || []).length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-5 text-sm text-muted-foreground">
                IPs não resolvidos
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <div className="grid md:grid-cols-2 gap-x-8 mt-4">
          <div>
            <Field label="WAF / CDN" value={(i.waf_cdn || []).join(', ') || 'não identificado'} />
            <Field label="Servidor" value={i.server_header} />
            <Field label="Registrar" value={i.domain_registrar} />
            <Field label="Titular" value={i.domain_registrant} />
          </div>
          <div>
            <Field label="Criado em" value={i.domain_created} />
            <Field label="Expira em" value={i.domain_expires} />
            <Field label="Status do registro" value={i.domain_status} />
            <Field label="Nameservers" value={(i.domain_nameservers || []).join(', ') || '—'} />
          </div>
        </div>
      </Section>

      {/* g) Subdomínios */}
      <Section title={`Subdomínios descobertos (${i.subdomains_total ?? 0})`} icon={ArrowUpRight} action={
        unmonitored.length > 0 && user?.role !== 'client' ? (
          <Button size="sm" onClick={() => promote(unmonitored.map(s => s.name))}
            data-testid="promote-bulk-btn">
            Promover {unmonitored.length} não monitorados
          </Button>
        ) : null
      }>
        {i.subdomains_note && <p className="text-xs text-amber-500 mb-2">{i.subdomains_note}</p>}
        <Table data-testid="dossier-subdomains">
          <TableHeader><TableRow>
            <TableHead>Subdomínio</TableHead><TableHead>Apontamentos</TableHead>
            <TableHead className="text-center">HTTPS</TableHead><TableHead className="text-center">Monitorado</TableHead>
            <TableHead className="text-right">Ação</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data.subdomains || []).map(s => (
              <TableRow key={s.name}>
                <TableCell className="font-mono text-xs">{s.name}</TableCell>
                <TableCell className="text-xs">{((s.records || {}).A || []).join(', ') || '—'}</TableCell>
                <TableCell className="text-center text-xs">{s.https_status || '—'}</TableCell>
                <TableCell className="text-center">
                  {s.monitored ? <Badge variant="default">Sim</Badge> : <Badge variant="outline">Não</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  {!s.monitored && (
                    <Button size="sm" variant="outline" onClick={() => promote([s.name])}
                      data-testid={`promote-${s.name}`}>Monitorar</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(data.subdomains || []).length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-5 text-sm text-muted-foreground">
                Nenhum subdomínio ativo encontrado nos logs públicos de certificados
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Section>

      {/* h) Linha do tempo */}
      <Section title="Linha do tempo de mudanças" icon={History} action={
        <Select value={eventKind} onValueChange={setEventKind}>
          <SelectTrigger className="w-[190px] h-8" data-testid="dossier-event-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(EVENT_KINDS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      }>
        <div className="space-y-2" data-testid="dossier-timeline">
          {events.slice(0, 80).map(e => (
            <div key={e.id} className="flex gap-3 items-start border-b border-border/50 pb-2 last:border-0">
              <span className="text-xs text-muted-foreground w-32 shrink-0">{fmtDate(e.at)}</span>
              <Badge variant="outline" className="text-xs shrink-0">{EVENT_KINDS[e.kind] || e.kind || '—'}</Badge>
              <div className="text-sm">
                <span className="font-medium">{e.field}</span>
                <span className="text-muted-foreground"> · {e.from} → {e.to}</span>
              </div>
            </div>
          ))}
          {events.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nenhuma mudança registrada para o filtro selecionado
            </p>
          )}
        </div>
      </Section>
    </div>
  );
}
